/**
 * Supply Chain Management — data model.
 *
 * Phase 1 of the SCM pack. Follows the SC2 site-canonical +
 * per-report-override + `*AtGen` snapshot pattern so the supply chain plan
 * stays editable on the site while individual reports remain reproducible.
 *
 * Resolution waterfall (mirrors `resolveSiteConfig` for projectIntent):
 *
 *   report.supplyChainPlanAtGen        ← STAMPED ONCE at generateTypedReport time
 *     ↑ derived at gen-time from
 *   report.supplyChainPlanOverride     ← optional per-report opt-in
 *     ↑ overrides
 *   site.supplyChainPlan                ← canonical, editable from SupplyChainPanel
 *
 * The display + LLM prompt builders read `supplyChainPlanAtGen` so prior
 * reports stay frozen after the site-level plan drifts.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Categories
// ─────────────────────────────────────────────────────────────────────────────

export type SCMCategory =
  | 'power-generation'       // SMR / Gas turbines / PV inverters / wind turbines
  | 'power-conversion'       // Transformers, switchgear, HVDC, GSU
  | 'energy-storage'         // BESS
  | 'cooling'                // Cooling towers, DLC, CRAH
  | 'data-center-it'         // Racks, UPS, PDU, structured cabling inside the hall
  | 'connectivity'           // Long-haul fiber, IX cross-connects
  | 'civil-construction'     // Concrete pours, structural steel, foundations
  | 'logistics'              // Heavy haul, port handling, rail, DOT permits
  | 'commissioning';         // Specialty NDE, startup services

/** Ordered list of all categories. Stable order — the BoM tab groups by it. */
export const SCM_CATEGORIES: readonly SCMCategory[] = [
  'power-generation',
  'power-conversion',
  'energy-storage',
  'cooling',
  'data-center-it',
  'connectivity',
  'civil-construction',
  'logistics',
  'commissioning',
] as const;

/** Human-readable labels for grouping headers. */
export const SCM_CATEGORY_LABELS: Record<SCMCategory, string> = {
  'power-generation':   'Power Generation',
  'power-conversion':   'Power Conversion',
  'energy-storage':     'Energy Storage',
  'cooling':            'Cooling',
  'data-center-it':     'Data Center IT',
  'connectivity':       'Connectivity',
  'civil-construction': 'Civil & Construction',
  'logistics':          'Logistics',
  'commissioning':      'Commissioning',
};

// ─────────────────────────────────────────────────────────────────────────────
// Units / status / provenance enums
// ─────────────────────────────────────────────────────────────────────────────

export type BomUnit = 'each' | 'kVA' | 'MW' | 'MWh' | 'm' | 'm2' | 'ton' | 'm3' | 'lot';

export type BomItemStatus =
  | 'planned'
  | 'rfq-out'
  | 'quoted'
  | 'ordered'
  | 'in-transit'
  | 'on-site'
  | 'commissioned';

// 'catalog' (#30 finding) — price came from the equipment catalog
// (EquipmentCatalogEntry.indicativeUnitCostUsd / inline supplier SKU), distinct
// from 'industry-baseline' (curated baseline DB) and 'manual' (hand-entered).
// Without it, catalog-sourced lines were mislabeled as 'industry-baseline',
// erasing the provenance distinction the directory + provenance surfaces rely on.
export type PriceSource = 'industry-baseline' | 'manual' | 'quote' | 'catalog';
export type LeadTimeSource = 'industry-baseline' | 'manual' | 'quote' | 'unknown';

export type BomCriticality = 'critical-path' | 'critical' | 'standard';
export type BomSubstitutability = 'sole-source' | 'limited' | 'commodity';

export type BomItemProvenance = 'auto-template' | 'manual' | 'uploaded-bom';

export type QuoteStatus = 'open' | 'awarded' | 'rejected' | 'expired';

export type ProductionSlotStatus = 'unknown' | 'available' | 'limited' | 'unavailable' | 'reserved';

export interface SupplyChainAttachmentRef {
  documentId: string;
  fileName?: string;
  uploadedAt?: string;
  notes?: string;
}

export interface PaymentMilestone {
  label: string;
  percent?: number;
  amountUsd?: number;
  trigger?: string;
  dueAt?: string;
}

export interface DeliveryReceipt {
  schemaVersion: 1;
  documentId: string;
  fileName: string;
  mimeType?: string;
  receivedAt: string;
  receivedByUid: string;
  receivedByLabel?: string;
  lat?: number;
  lng?: number;
  notes?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core entities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-line cost-timing (#29, William) — turns a flat, untimed BoM line into a
 * pair of real cash events so Finance can build a CAPEX drawdown curve across
 * construction years instead of dropping the whole line cost on one date.
 *
 * Each field maps to a real cash event:
 *   - `depositBasis` + (`depositPct` | `depositAmountUsd`) — the up-front
 *     payment made when the order is placed. EITHER a percentage of the line
 *     cost (`pct-of-cost`, reading `depositPct`) OR a fixed USD figure
 *     (`fixed-usd`, reading `depositAmountUsd`). Resolve with
 *     `bomItemDepositUsd()` — never read the raw fields directly, so the
 *     basis/clamp logic stays in one place.
 *   - `orderLeadMonths` — months BEFORE the line's on-site / delivery date that
 *     the order is placed and the deposit cash leaves the account. This is the
 *     hook that lets Finance spread CAPEX backward across earlier construction
 *     years: deposit-date = (required/expected on-site date) − orderLeadMonths.
 *   - `balanceTiming` — when the non-deposit balance (total − deposit) is paid:
 *     `at-delivery` (on receipt / on-site) or `at-cod` (held to commercial
 *     operation date, e.g. retention / performance holdback).
 *
 * All fields optional so existing untimed BoMs keep working unchanged; a line
 * with no `costTiming` simply has a 0 deposit and its full cost lands on its
 * delivery date (legacy behaviour).
 */
export interface BomItemCostTiming {
  /** How the deposit is expressed: a % of the line cost, or a fixed USD figure. */
  depositBasis?: 'pct-of-cost' | 'fixed-usd';
  /** Deposit as a percentage of `totalCostUsd`, 0..100. Used when basis = 'pct-of-cost'. */
  depositPct?: number;
  /** Deposit as a fixed USD amount. Used when basis = 'fixed-usd'. */
  depositAmountUsd?: number;
  /**
   * Months BEFORE the line's on-site / delivery date that the order is placed
   * and the deposit is paid. Lets Finance spread CAPEX across construction years.
   */
  orderLeadMonths?: number;
  /** When the non-deposit balance is paid: on delivery, or held to COD. */
  balanceTiming?: 'at-delivery' | 'at-cod';
}

export interface OemGenerationBasisSnapshot {
  registerSchemaVersion: number;
  registerCompiledAt: string;
  registerDigest: string;
  recordDigest: string;
  productId: string;
  performanceCaseId: string;
  manufacturer: string;
  model: string;
  variant: string;
  technology: string;
  packageScope: string;
  electricalOutputKw: number;
  ratingScope: string;
  frequencyHz: number[];
  duty: string;
  sourceIds: string[];
  supplierIds: string[];
  footprintAuthority: string;
  physicalReferenceId: string | null;
  authority: 'research-only' | 'simulation-admitted';
  simulationAdmissionIds: string[];
}

export interface OemElectricalMachineBasisSnapshot {
  registerSchemaVersion: number;
  registerCompiledAt: string;
  registerDigest: string;
  recordDigest: string;
  machineId: string;
  ratingCaseId: string;
  manufacturer: string;
  family: string;
  model: string;
  variant: string;
  ratedKva: number;
  ratedVoltageKv: number;
  frequencyHz: 50 | 60;
  ratedPowerFactor: number | null;
  poles: number;
  ratedSpeedRpm: number;
  sourceIds: string[];
  supplierIds: string[];
  authority: 'research-only';
}

export interface BomItem {
  id: string;
  category: SCMCategory;
  /**
   * Detailed free-text description used by EO canvas sizing extraction.
   * Prefer this over name/spec when available.
   */
  description?: string;
  /** Short equipment name, e.g. "Step-up transformer 132/22 kV 100 MVA". */
  name: string;
  /** Free-text spec line — vendor-readable, e.g. "ANSI/IEEE C57.12.00, 65°C rise". */
  spec: string;
  quantity: number;
  unit: BomUnit;
  status: BomItemStatus;

  /**
   * BOM-CAPEX (#28) — net electrical power contributed by ONE unit of this
   * line item, in MW. Optional and additive: only meaningful for
   * power-producing generation equipment (reciprocating engines, gas
   * turbines, SMR modules, PV inverter strings, etc.). When set, the
   * BOM-derived generation capacity for the line is `quantity * powerPerUnitMw`
   * and the campus generation capacity is the Σ over all generation lines
   * (see `bomGenerationCapacityMw`). Leave undefined for balance-of-plant /
   * non-generating items so existing BoMs are unaffected and never
   * mis-attributed as generation capacity.
   *
   * Basis: net per-unit nameplate MW (e.g. a 2 MW recip genset → 2). The
   * rollup multiplies by `quantity`, never by anything else, so units never
   * double-count. MW only — convert to kW (×1000) at the $/kW boundary.
   */
  powerPerUnitMw?: number;

  /** Stable source-catalog identity used to create this line, when applicable. */
  equipmentCatalogEntryId?: string;
  /** Immutable, exact OEM duty snapshot. Never reconstructed from free text. */
  oemGenerationBasis?: OemGenerationBasisSnapshot;
  /** Immutable, exact OEM alternator rating snapshot. */
  oemElectricalMachineBasis?: OemElectricalMachineBasisSnapshot;

  /**
   * BOM-ROLLUP (mutually-exclusive alternatives) — optional id grouping
   * several BoM lines that are MUTUALLY-EXCLUSIVE procurement ALTERNATIVES
   * for the SAME scope (e.g. three competing Phase-1 prime-mover options:
   * CAT recip vs Solar Titan vs GE TM2500). Lines that share an
   * `optionGroupId` are NOT additive — exactly ONE will actually be bought.
   *
   * The cost / capacity rollups (`itemizedCapexFromBom`,
   * `computePerPhaseCapexFromBom`, `bomGenerationCapacityMw`) count only the
   * SELECTED option per group (see `selectActiveBomItems`) so three $240M
   * alternatives roll up as ONE option's cost, never their $720M sum.
   *
   * Leave undefined for every ordinary, additive line (balance-of-plant,
   * the single chosen prime mover, etc.). Legacy BoMs with no `optionGroupId`
   * on any line behave EXACTLY as before — every line is additive.
   */
  optionGroupId?: string;
  /**
   * BOM-ROLLUP — within an `optionGroupId`, marks THIS line as the selected
   * (baseline) alternative that the rollups should count. At most one line
   * per group should set this `true`; if none do (or several do), the rollup
   * falls back to a deterministic default: the cheapest grounded option
   * (lowest finite `totalCostUsd`), breaking ties by BoM order. Ignored when
   * `optionGroupId` is undefined.
   */
  isSelectedAlternative?: boolean;

  // Pricing
  /** Unit price in USD. `null` means "no industry-baseline default available". */
  unitCostUsd: number | null;
  /** Derived = quantity * unitCostUsd. `null` when unitCostUsd is null. */
  totalCostUsd: number | null;
  priceSource: PriceSource;
  /** Where the price came from. baselineSource cites the curated DB entry;
   *  quoteId references a Quote when status >= 'quoted'. */
  priceProvenance?: { quoteId?: string; baselineSource?: string };

  /**
   * Per-line cost-timing (#29) — deposit basis + order lead + balance timing
   * so Finance can build a CAPEX drawdown curve. Optional; absent = the legacy
   * untimed behaviour (0 deposit, full cost on the delivery date). See
   * `BomItemCostTiming` and `bomItemDepositUsd()`.
   */
  costTiming?: BomItemCostTiming;

  // Schedule
  leadTimeMonthsLow: number;
  leadTimeMonthsHigh: number;
  leadTimeSource: LeadTimeSource;
  /** ISO date. Derived from project COD - install duration. */
  requiredOnSiteDate?: string;
  /** ISO date. Derived from order date + lead time. */
  expectedOnSiteDate?: string;

  // Suppliers
  shortlistedSupplierIds?: string[];
  awardedSupplierId?: string;

  // Compliance & quality
  /** e.g. ['NQA-1', 'ASME Section III, Class 1']. Free-form strings — the
   *  compliance certification DB (SCM3) provides the canonical taxonomy
   *  but BoM items can carry ad-hoc requirements too. */
  complianceRequirements?: string[];

  // Commercial baseline (BoM-level defaults; quote-level values override)
  baselineDepositAmountUsd?: number;
  baselinePaymentTerms?: string;
  baselineFreightTerms?: string;
  baselineQuoteValidityDays?: number;
  baselineDeliveryWindowStart?: string;
  baselineDeliveryWindowEnd?: string;
  baselineCertificationComplianceConfirmed?: boolean;
  baselineCertificationComplianceNotes?: string;
  baselineProductionSlotStatus?: ProductionSlotStatus;
  baselineProductionSlotNotes?: string;
  baselineCapacityAvailability?: ProductionSlotStatus;
  baselineCapacityAvailabilityNotes?: string;
  attachments?: SupplyChainAttachmentRef[];
  awardAttachments?: SupplyChainAttachmentRef[];

  // Risk
  criticality: BomCriticality;
  substitutability: BomSubstitutability;

  // Customization (#104) — optional visual overrides surfaced on the sim
  // canvas node so a BoM line can show its real supplier brand / equipment
  // photo / site footprint instead of the generic component glyph. ALL three
  // are rendered as `<img src>` and MUST be passed through `validateImageUrl`
  // (#111) before rendering — never trust the raw stored URL.
  /** Supplier brand mark (https / origin-relative media route / data:image). */
  supplierLogoUrl?: string;
  /** Real-world equipment photo or rendering for the node header. */
  customImage?: string;
  /** Real-world physical footprint / plot image (e.g. nameplate or site plan). */
  footprintImage?: string;

  // Provenance
  derivedFrom: BomItemProvenance;
  /** SCM12 marker replacing notes-prefix hacks. */
  reviewFlag?: 'quote-available';
  /** SCM19 delivery confirmation evidence. */
  deliveryReceipt?: DeliveryReceipt;
  notes?: string;

  /**
   * PP pack PP7 — when the owning project has phased rollout
   * (`intent.phases`), each generated line is tagged with the phase id
   * it was generated for. Undefined for legacy single-phase BoMs so
   * existing data continues to work unchanged.
   */
  phaseId?: string;
  /**
   * Multi-Parcel Site Boundary MP6 — when the BoM line is attributed
   * to a specific parcel (via PP phase → parcel mapping in the future
   * composition), this records the `SiteBoundaryParcel.id` so
   * per-parcel transformer / water / grid distance modelling can
   * identify which parcel an order belongs to. Unset = falls back to
   * the aggregate centroid (single-parcel behaviour).
   */
  parcelId?: string;
}

/**
 * Qualitative pricing band — coarse 4-point scale used by the supplier
 * directory to telegraph where a vendor sits in the price spectrum without
 * disclosing per-quote numbers. SCM10 surfaces this on supplier cards.
 *
 *   - 'budget'         — lowest 1/4 of market (often Chinese / Indian OEMs)
 *   - 'value'          — mid-market, often Korean / Mexican / EU mid-tier
 *   - 'premium'        — top-tier western OEM list price (GE, Siemens, ABB)
 *   - 'ultra-premium'  — bespoke / nuclear-grade / sole-source
 */
export type SupplierPricingIndex = 'budget' | 'value' | 'premium' | 'ultra-premium';

/**
 * Stable id of a `ComplianceCertification` taxonomy entry (e.g. `'nqa-1'`,
 * `'asme-section-iii-class-1'`). Stringly-typed for now — TS doesn't let us
 * narrow this to the literal union of curated ids without a costly type-level
 * dependency on `supplyChainBaselines.ts`, and we want freeform fallbacks to
 * continue to type-check for ad-hoc requirements that pre-date the taxonomy.
 *
 * Convention: SCM13 normalises supplier certifications to these ids; any
 * value that's not a known id is kept as a freeform string and logged at
 * module-load time by `supplyChainBaselines`.
 */
export type ComplianceCertificationId = string;

/**
 * Supplier certification entry. Two equivalent shapes:
 *
 *   - A plain `ComplianceCertificationId` string ('nqa-1') — baseline form,
 *     no expiry info.
 *   - An object `{ id, expiresAt? }` where `expiresAt` is an ISO date
 *     (YYYY-MM-DD or full ISO timestamp). SCM14 uses `expiresAt` to flag
 *     suppliers whose certifications lapse within the 90-day horizon.
 *
 * The union lets SCM13 (typed taxonomy ids) and SCM14 (expiration tracking)
 * coexist without forcing a backfill. Use `normaliseCertification()` at
 * read time to get a uniform `{ id, expiresAt? }` shape.
 */
export type SupplierCertification = ComplianceCertificationId | { id: ComplianceCertificationId; expiresAt?: string };

export interface SupplierContact {
  role: 'sales' | 'technical' | 'contracts' | 'general';
  name?: string;
  /** @pii email */
  email?: string;
  phone?: string;
}

export type PartnershipStatus =
  | 'strategic-agreement'
  | 'loi'
  | 'mou'
  | 'nda'
  | 'none';

export interface SupplierPartnership {
  status: PartnershipStatus;
  agreementLabel?: string;
  effectiveDate?: string;
  expiresAt?: string;
  documentId?: string;
  notes?: string;
}

export interface SupplierDataFreshness {
  lastUpdatedAt: string;
  source: string;
}

export interface EquipmentCatalogEntry {
  id: string;
  name: string;
  spec: string;
  scmCategory: SCMCategory;
  tags: string[];
  supplierIds?: string[];
  indicativeUnitCostUsd?: number;
  leadTimeMonths?: { low: number; high: number };
  capacityRating?: string;
  /** Net/gross boundary is preserved separately in `oemGenerationBasis`. */
  powerPerUnitMw?: number;
  lastVerifiedAt?: string;
  source: 'baseline' | 'admin' | 'research-queue' | 'oem-source-register';
  /** Pointer only; BOM creation resolves and snapshots the immutable register record. */
  oemGenerationProductId?: string;
  oemPerformanceCaseId?: string;
  /** Pointer to a standalone OEM alternator record and exact rating case. */
  oemElectricalMachineId?: string;
  oemElectricalRatingCaseId?: string;
  /** SMR SKUs linked to vendor catalog ids (v2). */
  reactorVendorIds?: string[];
  /**
   * G&I screening-envelope join key (rows seeded by seed:oem-catalog). The
   * versioned `GENERATION_EQUIPMENT_CATALOG` stays the numeric authority; this
   * row only points at it so catalog browsers can offer the real envelope.
   */
  generationScreeningId?: string;
}

export interface Supplier {
  id: string;
  name: string;
  categories: SCMCategory[];
  hq: { country: string; city?: string };
  /** Certifications held, referenced by taxonomy id (SCM13). Each entry is
   *  either a plain `ComplianceCertificationId` string OR an object
   *  `{ id, expiresAt? }` for SCM14 expiration tracking — see
   *  `SupplierCertification`. Freeform strings remain valid as a migration
   *  fallback for ids not yet in the canonical taxonomy; `supplierMatching`
   *  normalises via the alias map. */
  certifications?: SupplierCertification[];
  typicalLeadMonths?: { low: number; high: number };
  /** Curated reliability tier — tier-1 = decades of qualified deliveries,
   *  tier-3 = newer entrant. Used for shortlist ranking. */
  reliabilityTier?: 'tier-1' | 'tier-2' | 'tier-3';
  /**
   * Optional financial-health tier. Admin-curated for now; intended to
   * be populated automatically by the Dun & Bradstreet integration that
   * lands in SCM Phase 3 (where licensing permits). For the current
   * Phase 2 ship, treat 'unknown' as the default for entries that have
   * not been hand-vetted against a public credit rating
   * (Moody's / S&P / Fitch) or D&B PAYDEX equivalent.
   */
  financialHealthTier?: 'investment-grade' | 'speculative' | 'distressed' | 'unknown';
  /** Optional public homepage URL — surfaced as the supplier card link. */
  website?: string;
  /** Logo URL — hosted storage or external HTTPS (admin-curated). */
  logoUrl?: string;
  /**
   * Short human-readable annual production capacity statement, e.g.
   * `"12 SMR modules/yr"` or `"~40 GW PV modules/yr"`. SCM10 — left flat
   * (not per-category) since most directory entries operate at one
   * dominant scale even when they cover multiple SCM categories. Optional
   * so legacy entries don't all need backfill in one go.
   */
  capacity?: string;
  /**
   * Qualitative pricing band. SCM10 — surfaced on supplier cards so
   * procurement can sort the directory by cost posture without exposing
   * per-quote numbers. Optional.
   */
  typicalPricingIndex?: SupplierPricingIndex;
  notes?: string;
  contacts?: SupplierContact[];
  partnership?: SupplierPartnership;
  /** Inline catalog SKUs when not stored in platform equipment_catalog. */
  catalog?: EquipmentCatalogEntry[];
  dataFreshness?: SupplierDataFreshness;
}

/**
 * Normalise a `SupplierCertification` to its `{ id, expiresAt? }`
 * object form. Pure; safe to call on hot paths.
 */
export function normaliseCertification(
  cert: SupplierCertification,
): { id: string; expiresAt?: string } {
  if (typeof cert === 'string') return { id: cert };
  return { id: cert.id, expiresAt: cert.expiresAt };
}

// ─────────────────────────────────────────────────────────────────────────────
// Supplier risk assessment (SCM14)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derived supplier-level risk view. Built by
 * `supplierRiskAssessment(supplier, now)` in `src/lib/supplyChainRisks.ts`
 * and consumed by the Suppliers tab to surface the colored overall-tier
 * pill plus the per-component breakdown popover.
 */
export interface SupplierRiskAssessment {
  /** Tier derived from HQ country via the geopolitical risk rubric. */
  geopoliticalRiskLevel: 'low' | 'medium' | 'high' | 'critical';
  /** True when the geopolitical rubric had no entry for the HQ country
   *  and fell through to the conservative 'medium' default. UI surfaces
   *  this as a "needs review" caveat. */
  geopoliticalMatched: boolean;
  /** Count of certifications with an `expiresAt` within the 90-day
   *  horizon from `now`. Already-expired certs are included in the
   *  count (treated as worse, not better, than upcoming expiries). */
  certsExpiringSoonCount: number;
  /** Count of certifications that have already lapsed at `now`. */
  certsExpiredCount: number;
  /** Pass-through of the curated financial-health tier (defaults to
   *  'unknown' when the supplier has no value set). */
  financialHealthTier: NonNullable<Supplier['financialHealthTier']>;
  /** Reduction of the three component tiers — the worst-of-three. */
  overallTier: 'low' | 'medium' | 'high' | 'critical';
}

export type QuoteProvenanceSource =
  | 'curator-parsed'
  | 'manual-entry'
  | 'rfq-response'
  | 'web-scraped'
  | 'supplier-portal-import'
  | 'unknown';

export interface QuoteProvenance {
  source: QuoteProvenanceSource;
  /** Links to the uploaded PDF (SiteDocument id). */
  sourceDocId?: string;
  /** How many fields were auto-extracted from the source document. */
  extractedFieldCount?: number;
  /** Total fields the quote needs (denominator for extraction ratio). */
  totalFieldCount?: number;
  /** ISO timestamp of last human verification of this quote's data. */
  lastVerifiedAt?: string;
  /** True if the supplier signature was detected in the source doc. */
  supplierSigned?: boolean;
  /** Country of origin as claimed on the quote. */
  countryOfOrigin?: string;
  /** Warranty terms as stated. */
  warrantyTerms?: string;
}

export interface Quote {
  id: string;
  bomItemId: string;
  supplierId: string;
  unitCostUsd: number;
  leadTimeMonths: number;
  /** ISO timestamp the quote was received. */
  receivedAt: string;
  /** ISO timestamp the quote expires (vendor validity period). */
  expiresAt: string;
  status: QuoteStatus;
  /** Optional SiteDocument id linking the source quote PDF. */
  documentId?: string;
  /** Payment / freight terms when extracted (e.g. "Net 30", "DDP site"). */
  paymentTerms?: string;
  freightTerms?: string;
  depositAmountUsd?: number;
  paymentSchedule?: PaymentMilestone[];
  deliveryWindowStart?: string;
  deliveryWindowEnd?: string;
  certificationComplianceConfirmed?: boolean;
  certificationComplianceNotes?: string;
  productionSlotStatus?: ProductionSlotStatus;
  productionSlotNotes?: string;
  capacityAvailabilityStatus?: ProductionSlotStatus;
  capacityAvailabilityNotes?: string;
  extendedPriceUsd?: number;
  attachments?: SupplyChainAttachmentRef[];
  /** Vendor-stated validity window in days (SCM8 PDF-extracted). */
  validityDays?: number;
  /** ISO-4217 code of the quoted price when not USD (SCM8 PDF-extracted). */
  currency?: string;
  /** Evidence provenance — tracks how this quote's data was sourced. */
  provenance?: QuoteProvenance;
  notes?: string;
}

/**
 * Compliance certification taxonomy entry — populated from SCM3's
 * `complianceCertifications` baseline. Stored on the plan as a snapshot
 * of which certifications applied at planning time (so a report rerun
 * after a taxonomy update doesn't silently drop a requirement).
 */
export interface ComplianceCertification {
  /** Stable id, e.g. 'nqa-1', 'asme-section-iii-class-1'. */
  id: string;
  /** Human-readable name, e.g. 'NQA-1'. */
  name: string;
  /** Short description / scope of the standard. */
  description: string;
  /** SCM categories the certification applies to. */
  applicableCategories: SCMCategory[];
  /** Authority / issuer (e.g. 'ASME', 'IEEE', 'IEC', 'ISO'). */
  authority?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// SupplyChainPlan — the SED-like aggregate written at site level + snapshotted
// per report.
// ─────────────────────────────────────────────────────────────────────────────

/** Owner-declared material scope that is not represented by a priced BOM row.
 * Numeric values remain unknown; linked source IDs are private lineage only. */
export interface SupplyChainMaterialScopeLimitation {
  id: string;
  title: string;
  description: string;
  unknownFields: Array<'quantity' | 'cost' | 'lead-time'>;
  costTreatment: 'excluded-unpriced';
  sourceDocumentIds?: string[];
}

export interface SupplyChainPlan {
  /** Bill of materials line items. Order is preserved (auto-template runs
   *  emit a stable order; user edits append). */
  bom: BomItem[];
  /** Site-level shortlist of suppliers being considered / engaged. The
   *  global supplier directory lives in the curated baseline JSON (SCM3);
   *  this list is the project's working set. */
  suppliers: Supplier[];
  /** Quote ledger — every quote ever received for this site's BoM items,
   *  including superseded ones (status='rejected' / 'expired'). */
  quotes: Quote[];
  /** Unknown/excluded equipment scope; never creates a numeric or priced BOM row. */
  materialScopeLimitations?: SupplyChainMaterialScopeLimitation[];

  /** Schema version — bump when the BomItem shape changes incompatibly so
   *  reader code can detect old snapshots. Starts at 1. */
  version: number;

  /** ISO timestamp of the initial auto-generation (or the first manual save). */
  generatedAt: string;
  /** 'auto' = produced by `generateBomFromContext`. 'manual' = produced by
   *  user upload / hand-entry without going through the generator. */
  generatedBy: 'auto' | 'manual';

  /** ISO timestamp of the most recent write to any part of the plan. */
  updatedAt: string;
  /** UID / email of the user who made the most recent write. Mirrors the
   *  SC2 `siteConfigUpdatedBy` pattern. */
  updatedBy: string;

  /**
   * SC4-1 — ISO timestamp of the most recent BoM seed via
   * `generateBomFromIntent`. Distinct from `generatedAt` (initial creation)
   * — captures re-seeds triggered by intent / financial drift so the
   * Overview can render "Last seeded 3 hours ago" alongside the canonical
   * generated-at stamp.
   */
  lastSeededAt?: string;
  /** SC4-1 — UID / email of the user who triggered the most recent seed. */
  lastSeededBy?: string;
  /**
   * SC4-1 — Snapshot of the resolved Project Intent + Financial Assumptions
   * at the moment the BoM was seeded. Used by downstream provenance
   * surfaces (CT1 EvidenceLineage for SCM-derived claims; the diff panel
   * to show "what changed since the last seed").
   *
   * Deep-cloned at write time so subsequent edits to the live site doc
   * cannot retroactively mutate the snapshot. Optional — pre-SC4 plans
   * leave it undefined.
   */
  seedSource?: {
    intentSnapshotAtSeed: import('./siteRag').ProjectIntent;
    financialSnapshotAtSeed?: import('./financialAssumptions').FinancialAssumptions;
  };
}

/** Current plan schema version — bump on incompatible BomItem changes. */
export const SUPPLY_CHAIN_PLAN_VERSION = 1;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — small, side-effect-free derivations exposed to UI + tests.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Re-compute a BomItem's `totalCostUsd` from its current `quantity` and
 * `unitCostUsd`. Returns `null` when `unitCostUsd` is null. Pure function;
 * the inline-edit UI calls this on every keystroke.
 */
export function recomputeTotalCost(item: Pick<BomItem, 'quantity' | 'unitCostUsd'>): number | null {
  if (item.unitCostUsd == null || !Number.isFinite(item.unitCostUsd)) return null;
  if (!Number.isFinite(item.quantity)) return null;
  return item.quantity * item.unitCostUsd;
}

/**
 * #29 — resolve the deposit (the up-front cash event) for ONE BoM line, in USD.
 *
 *   - basis 'pct-of-cost' → `totalCostUsd * depositPct / 100`
 *   - basis 'fixed-usd'   → `depositAmountUsd`
 *   - no timing / no basis / non-finite inputs → 0
 *
 * The result is clamped to `[0, totalCostUsd]` so a deposit can never exceed
 * the line cost (which would make the balance negative and corrupt the
 * drawdown curve) nor go negative. Pure; safe on hot paths. When
 * `totalCostUsd` is null/non-finite there is no cost basis to draw against, so
 * a pct-of-cost deposit resolves to 0; a fixed-usd deposit still has no ceiling
 * to clamp against, so it is only floored at 0 (Finance treats an unpriced line
 * as not yet contributing to the curve).
 */
export function bomItemDepositUsd(item: BomItem): number {
  const timing = item.costTiming;
  if (!timing || !timing.depositBasis) return 0;

  const total = item.totalCostUsd;
  const hasTotal = total != null && Number.isFinite(total);

  let deposit = 0;
  if (timing.depositBasis === 'pct-of-cost') {
    const pct = timing.depositPct;
    if (!hasTotal || pct == null || !Number.isFinite(pct)) return 0;
    deposit = (total as number) * (pct / 100);
  } else {
    // 'fixed-usd'
    const amount = timing.depositAmountUsd;
    if (amount == null || !Number.isFinite(amount)) return 0;
    deposit = amount;
  }

  if (!Number.isFinite(deposit) || deposit <= 0) return 0;
  // Cap at the line cost when we have one; an unpriced line has no ceiling.
  return hasTotal ? Math.min(deposit, total as number) : deposit;
}

/**
 * BOM-CAPEX (#28) — the BomItem categories whose lines contribute net
 * electrical GENERATION capacity. A `powerPerUnitMw` value is only rolled
 * into `bomGenerationCapacityMw` when the line sits in one of these
 * categories, so a balance-of-plant line that happens to carry a stray
 * `powerPerUnitMw` can never inflate generation capacity, and a generation
 * line's cost (rolled up separately via `totalCostUsd`) is never confused
 * with its capacity.
 *
 * Single source of truth: `power-generation` is the canonical generation
 * bucket (SMR / gas turbines / reciprocating engines / PV inverters / wind).
 */
export const BOM_GENERATION_CATEGORIES: readonly SCMCategory[] = ['power-generation'] as const;

/** True when this line belongs to a generation (power-producing) category. */
export function isGenerationBomItem(item: Pick<BomItem, 'category'>): boolean {
  return BOM_GENERATION_CATEGORIES.includes(item.category);
}

/**
 * BOM-ROLLUP — collapse mutually-exclusive procurement ALTERNATIVES down to
 * the ONE active option per `optionGroupId`, so additive rollups never sum
 * competing options that can't all be bought (e.g. three rival Phase-1 prime
 * movers @ $240M each must roll up as $240M, not $720M).
 *
 * Rules (pure; preserves input order; never throws):
 *   - Lines with NO `optionGroupId` are ALWAYS kept (ordinary additive lines).
 *     A legacy BoM where no line carries an `optionGroupId` is returned as-is,
 *     so behaviour is byte-for-byte unchanged from before this feature.
 *   - For each `optionGroupId`, exactly ONE line survives — the SELECTED
 *     alternative, chosen by this precedence:
 *       1. the line with `isSelectedAlternative === true` (first one wins if
 *          several are mis-flagged);
 *       2. else the cheapest GROUNDED option — lowest finite `totalCostUsd`
 *          (ties broken by original BoM order);
 *       3. else (no priced option) the FIRST line in the group.
 *   - The surviving line is emitted at the position of the group's FIRST line,
 *     so downstream order (category grouping, phase attribution) is stable.
 *
 * `Pick`-typed so the capacity rollup (which only needs a subset of fields)
 * and the cost rollups (which need `totalCostUsd`) can both reuse it.
 */
export function selectActiveBomItems<
  T extends Pick<BomItem, 'optionGroupId' | 'isSelectedAlternative' | 'totalCostUsd'>,
>(items: readonly T[]): T[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  // Fast path + backward-compat: if NO line declares an option group, the BoM
  // is fully additive — return it untouched (and in original order).
  let anyGroup = false;
  for (const it of items) {
    if (it.optionGroupId != null && it.optionGroupId !== '') {
      anyGroup = true;
      break;
    }
  }
  if (!anyGroup) return [...items];

  // Bucket grouped lines (preserving order); pass ungrouped lines straight
  // through. We track each group's first-seen index so the winner re-occupies
  // that slot in the output.
  const groupMembers = new Map<string, T[]>();
  const groupFirstIndex = new Map<string, number>();
  const out: (T | null)[] = [];

  items.forEach((item, idx) => {
    const gid = item.optionGroupId;
    if (gid == null || gid === '') {
      out.push(item);
      return;
    }
    if (!groupMembers.has(gid)) {
      groupMembers.set(gid, []);
      groupFirstIndex.set(gid, out.length);
      out.push(null); // placeholder, filled with the winner below
    }
    groupMembers.get(gid)!.push(item);
  });

  for (const [gid, members] of groupMembers) {
    out[groupFirstIndex.get(gid)!] = selectGroupWinner(members);
  }

  return out.filter((x): x is T => x != null);
}

/** Pick the single active option from one mutually-exclusive group. */
function selectGroupWinner<
  T extends Pick<BomItem, 'isSelectedAlternative' | 'totalCostUsd'>,
>(members: T[]): T {
  // 1. Explicit selection wins (first, if several are mis-flagged true).
  const explicit = members.find((m) => m.isSelectedAlternative === true);
  if (explicit) return explicit;

  // 2. Cheapest grounded option (lowest finite totalCostUsd; ties → order).
  let cheapest: T | undefined;
  for (const m of members) {
    const cost = m.totalCostUsd;
    if (cost == null || !Number.isFinite(cost)) continue;
    if (cheapest == null || (cost as number) < (cheapest.totalCostUsd as number)) {
      cheapest = m;
    }
  }
  if (cheapest) return cheapest;

  // 3. No priced option — fall back to the first line in the group.
  return members[0];
}

/**
 * BOM-CAPEX (#28) — per-line BOM-derived generation capacity in MW:
 * `quantity * powerPerUnitMw`, but ONLY for generation-category lines with a
 * finite, positive `powerPerUnitMw`. Returns `null` for non-generation lines
 * or lines with no/invalid power-per-unit so callers can distinguish
 * "0 MW contributed" from "no capacity basis on this line".
 *
 * Worked example (William's source of truth): a recip line with
 * `quantity = 50`, `powerPerUnitMw = 2` ⇒ 100 MW.
 */
export function bomItemGenerationCapacityMw(
  item: Pick<BomItem, 'category' | 'quantity' | 'powerPerUnitMw'>,
): number | null {
  if (!isGenerationBomItem(item)) return null;
  const mwPerUnit = item.powerPerUnitMw;
  if (mwPerUnit == null || !Number.isFinite(mwPerUnit) || mwPerUnit <= 0) return null;
  if (!Number.isFinite(item.quantity) || item.quantity <= 0) return null;
  return item.quantity * mwPerUnit;
}

/**
 * BOM-CAPEX (#28) — campus generation capacity (MW) derived purely from the
 * BoM: Σ(quantity × powerPerUnitMw) over every generation-category line that
 * declares a power-per-unit. Returns `null` when NO generation line declares a
 * power-per-unit (so the caller can fall back to the manual / intent capacity
 * source rather than dividing CAPEX by a fabricated 0). Pure; never throws.
 *
 * Basis: net electrical nameplate MW. This is the divisor basis for the
 * BOM-native $/kW (capexTotalUsd / (bomCapacityMw × 1000)).
 *
 * BOM-ROLLUP: mutually-exclusive alternatives are collapsed to the ONE active
 * option per `optionGroupId` (via `selectActiveBomItems`) BEFORE summing, so
 * three rival 300 MW prime-mover options contribute 300 MW, not 900 MW.
 */
export function bomGenerationCapacityMw(
  plan: Pick<SupplyChainPlan, 'bom'> | null | undefined,
): number | null {
  if (!plan || !Array.isArray(plan.bom)) return null;
  let total = 0;
  let anyContribution = false;
  for (const item of selectActiveBomItems(plan.bom)) {
    const mw = bomItemGenerationCapacityMw(item);
    if (mw == null) continue;
    total += mw;
    anyContribution = true;
  }
  return anyContribution ? total : null;
}

/**
 * Construct an empty plan with the current schema version. Use this when
 * a site has no plan yet and a UI surface needs a placeholder it can
 * write into.
 */
export function emptySupplyChainPlan(opts: { updatedBy: string }): SupplyChainPlan {
  const now = new Date().toISOString();
  return {
    bom: [],
    suppliers: [],
    quotes: [],
    version: SUPPLY_CHAIN_PLAN_VERSION,
    generatedAt: now,
    generatedBy: 'manual',
    updatedAt: now,
    updatedBy: opts.updatedBy,
  };
}

/**
 * Type guard — checks whether a value looks structurally like a
 * SupplyChainPlan. Used by the SC2-style resolver waterfall when deciding
 * whether to fall through to the site-canonical value.
 */
export function isSupplyChainPlan(value: unknown): value is SupplyChainPlan {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<SupplyChainPlan>;
  return (
    Array.isArray(v.bom) &&
    Array.isArray(v.suppliers) &&
    Array.isArray(v.quotes) &&
    typeof v.version === 'number'
  );
}
